/**
 * The TP/SL modal's advanced controls: the trigger-source selector that sits
 * with the trigger inputs, and the size / limit-price fields the two checkboxes
 * reveal.
 *
 * Both pieces are presentational — every value and toggle comes from
 * `useTPSLAdvanced`, and every validator from `useTPSLRegisters`.
 */
import currency from "currency.js";
import {
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { TpslPriceSource } from "../../hooks/tpsl/tpsl.types";
import SegmentControl from "../shared/SegmentedControl";
import {
  FieldError,
  SourceHeader,
  SourceRow,
  SourceValues,
} from "./TPSLForPositionModal.helpers";
import {
  CloseFieldLabel,
  CloseInputRow,
  CloseInputUnit,
} from "./style";
import { QuickActions } from "../shared/NumericField";
import { activePreset } from "../../utils/amount/percent";
import { tpslSizeForPercent, tpslSizeMax } from "../../utils/amount/max/tpslSizeMax";
import { closeSteps } from "./ClosePositionModal.perpAction";
import { TPSLAdvanced } from "./useTPSLAdvanced";

/** 100 is MAX: the whole position. */
const TPSL_STOPS = [25, 50, 75, 100];

type TranslateFn = (key: string, options?: any) => string;

const formatPrice = (value: number | undefined, precision: number) =>
  value ? currency(value, { precision }).format() : "—";

interface ITriggerSourceProps {
  priceSource: TpslPriceSource;
  setPriceSource: (source: TpslPriceSource) => void;
  indexPrice?: number;
  lastPrice?: number;
  pricePrecision: number;
  t: TranslateFn;
}

export function TPSLTriggerSource({
  priceSource,
  setPriceSource,
  indexPrice,
  lastPrice,
  pricePrecision,
  t,
}: ITriggerSourceProps) {
  // No trades have streamed in yet (or the market has never traded) — label the
  // option for what the user CAN see instead of showing a phantom number.
  const lastLabel = lastPrice ? t("last_price") : t("last_price_unavailable");

  return (
    <SourceRow>
      <SourceHeader>
        <CloseFieldLabel style={{ whiteSpace: "nowrap" }}>
          {t("trigger_by")}
        </CloseFieldLabel>
        <SourceValues>
          <span>
            {t("index_price")}: {formatPrice(indexPrice, pricePrecision)}
          </span>
          <span>
            {t("last_price")}: {formatPrice(lastPrice, pricePrecision)}
          </span>
        </SourceValues>
      </SourceHeader>
      <div>
        <SegmentControl
          segments={[
            {
              value: "oracle",
              display: t("index_price"),
              textColor:
                priceSource === "oracle" ? TEXT_COLORS.one : TEXT_COLORS.three,
            },
            {
              value: "last",
              display: lastLabel,
              textColor:
                priceSource === "last" ? TEXT_COLORS.one : TEXT_COLORS.three,
            },
          ]}
          value={priceSource}
          onSelect={(v) => setPriceSource(v as TpslPriceSource)}
          config={{
            color: TEXT_COLORS.three,
            widthType: "fullWidth",
            backgroundColor: LAYER_COLORS.two,
            activeBackgroundColor: COLORS.highlight.four,
            borderRadius: "6px",
            button: { height: 32, fontSize: "12px" },
          }}
        />
      </div>
    </SourceRow>
  );
}

interface IAdvancedFieldsProps {
  advanced: TPSLAdvanced;
  closeSizeRegister: any;
  tpLimitPriceRegister: any;
  slLimitPriceRegister: any;
  errors: any;
  asset: string;
  amountPrecision: number;
  /**
   * The market's `amount_step`. Defaults to one unit of `amountPrecision`,
   * which is always on the step grid when the precision is no finer than it.
   */
  amountStep?: number;
  markPrice?: string | number;
  tpEnabled: boolean;
  slEnabled: boolean;
  isLong: boolean;
  t: TranslateFn;
}

export function TPSLAdvancedFields({
  advanced,
  closeSizeRegister,
  tpLimitPriceRegister,
  slLimitPriceRegister,
  errors,
  asset,
  amountPrecision,
  amountStep,
  markPrice,
  tpEnabled,
  slEnabled,
  isLong,
  t,
}: IAdvancedFieldsProps) {
  const { closeSize, closeSizeNum, positionAmount, positionAmountText } = advanced;
  const sizeError = errors?.tpslCloseSize;
  const notional = closeSizeNum * (Number(markPrice) || 0);
  const limitError = isLong
    ? t("limit_price_error_long")
    : t("limit_price_error_short");

  // MAX is the position exactly; the other stops floor onto the market's grid
  // (P2 `tpslSizeMax`). The float version zeroed small sizes and lit two chips.
  const step = amountStep && amountStep > 0 ? amountStep : 10 ** -amountPrecision;
  const steps = closeSteps({ amount_step: step, amount_precision: amountPrecision });
  const heldText = positionAmountText ?? String(positionAmount);
  const sizeMax = tpslSizeMax({ positionAmount: heldText, steps, unit: asset });
  const activeStop = sizeMax.ok
    ? activePreset(String(closeSize ?? ""), sizeMax.max, TPSL_STOPS, steps.amountStep)
    : null;

  return (
    <>
      {advanced.configureAmount && (
        <div style={{ marginTop: SPACING.three }}>
          <CloseFieldLabel>{t("close_size")}</CloseFieldLabel>
          <CloseInputRow error={Boolean(sizeError?.type)}>
            <input
              type="number"
              inputMode="decimal"
              placeholder="0"
              {...closeSizeRegister}
            />
            <CloseInputUnit>{asset}</CloseInputUnit>
          </CloseInputRow>
          <div style={{ marginTop: SPACING.two }}>
            <QuickActions
              active={activeStop}
              max={sizeMax}
              onSelect={(pct) =>
                advanced.setSizePercent(tpslSizeForPercent(heldText, pct, steps))
              }
            />
          </div>
          {sizeError?.type ? (
            <FieldError>{t("close_size_error")}</FieldError>
          ) : (
            <CloseFieldLabel style={{ marginTop: SPACING.two }}>
              {notional > 0
                ? `≈ ${currency(notional).format()} · ${t("close_size_help")}`
                : t("close_size_help")}
            </CloseFieldLabel>
          )}
        </div>
      )}

      {advanced.useLimitPrice && (tpEnabled || slEnabled) && (
        <div style={{ marginTop: SPACING.three }}>
          {tpEnabled && (
            <>
              <CloseFieldLabel>{t("tp_limit_price")}</CloseFieldLabel>
              <CloseInputRow error={Boolean(errors?.tpLimitPrice?.type)}>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...tpLimitPriceRegister}
                />
                <CloseInputUnit>USD</CloseInputUnit>
              </CloseInputRow>
              {errors?.tpLimitPrice?.type ? (
                <FieldError>{limitError}</FieldError>
              ) : null}
            </>
          )}
          {slEnabled && (
            <div style={{ marginTop: tpEnabled ? SPACING.three : 0 }}>
              <CloseFieldLabel>{t("sl_limit_price")}</CloseFieldLabel>
              <CloseInputRow error={Boolean(errors?.slLimitPrice?.type)}>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...slLimitPriceRegister}
                />
                <CloseInputUnit>USD</CloseInputUnit>
              </CloseInputRow>
              {errors?.slLimitPrice?.type ? (
                <FieldError>{limitError}</FieldError>
              ) : null}
            </div>
          )}
          <CloseFieldLabel style={{ marginTop: SPACING.two }}>
            {t("limit_price_help")}
          </CloseFieldLabel>
        </div>
      )}
    </>
  );
}
